import * as React from 'react';

import { FreightLaneSortOption } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  AsyncBoundary,
  Badge,
  Button,
  Content,
  DataGrid,
  DataGridColumn,
  Footer,
  HeaderIcons,
  LocalizedDate,
  Pagination,
  SearchField,
  Toolbar,
  type PaginationProps,
} from '@rematter/ui-web';
import { LucidePlus } from 'lucide-react';

import { FreightLaneDialog } from './FreightLaneDialog';

import { useFreightLanesListQuery } from '~/modules/transportation/hooks/queries/useFreightLanesListQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { FreightLaneRecord } from '~/modules/transportation/types';

const COLUMN_TO_SORT_MAP: Record<string, { asc: string; desc: string }> = {
  createdAt: {
    asc: 'CreatedAtAsc',
    desc: 'CreatedAtDesc',
  },
};

const formatDestination = (lane: FreightLaneRecord): string => {
  const parts = [
    lane.destinationCity,
    lane.destinationState,
    lane.destinationZipCode,
  ].filter(Boolean);
  if (parts.length === 0) return '-';
  return parts.join(', ');
};

export const FreightLanesListContent: React.FC = () => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [offset, setOffset] = React.useState<number>(0);
  const [limit, setLimit] = React.useState<number>(50);
  const [sorting, setSorting] = React.useState<FreightLaneSortOption[]>([
    'CreatedAtDesc',
  ]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editFreightLane, setEditFreightLane] =
    React.useState<FreightLaneRecord | null>(null);

  const { data, error, isLoading, isFetching, refetch } =
    useFreightLanesListQuery({
      filter: {},
      sorting,
      pagination: { limit, offset },
    });

  const freightLanes: FreightLaneRecord[] = React.useMemo(
    () => data?.freightLanes ?? [],
    [data?.freightLanes],
  );
  const total = data?.total ?? 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleEdit = React.useCallback((freightLane: FreightLaneRecord) => {
    setEditFreightLane(freightLane);
    setIsDialogOpen(true);
  }, []);

  const handleCreate = React.useCallback(() => {
    setEditFreightLane(null);
    setIsDialogOpen(true);
  }, []);

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setSorting(['CreatedAtDesc']);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setSorting([sortOption as FreightLaneSortOption]);
        }
      }
      setOffset(0);
    },
    [],
  );

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: FreightLaneRecord }) => {
      handleEdit(row);
    },
    [handleEdit],
  );

  const columns: DataGridColumn<FreightLaneRecord>[] = React.useMemo(
    () => [
      {
        key: 'carrierName',
        header: t.freightLanes.table.columns.carrier,
        width: 200,
        icon: HeaderIcons.Text,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center font-medium">
            {row.carrierName || '-'}
          </div>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        width: 90,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge
            color={row.freightLaneRouteId ? 'primary' : 'neutral'}
            size="small"
          >
            {row.freightLaneRouteId ? 'Route' : 'Area'}
          </Badge>
        ),
      },
      {
        key: 'destination',
        header: t.freightLanes.table.columns.destination,
        grow: 1,
        icon: HeaderIcons.Text,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="truncate text-neutral/70">
            {formatDestination(row)}
          </div>
        ),
      },
      {
        key: 'active',
        header: t.freightLanes.table.columns.status,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge color={row.active ? 'success' : 'neutral'} size="small">
            {row.active
              ? t.freightLanes.table.active
              : t.freightLanes.table.inactive}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        header: t.freightLanes.table.columns.createdAt,
        sortable: true,
        width: 140,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.freightLanes.emptyState.title,
      description: <span>{t.freightLanes.emptyState.description}</span>,
      centered: true,
    }),
    [t],
  );

  return (
    <AsyncBoundary
      loading={isLoading || isFetching}
      error={error}
      className="@FreightLanesListContent"
    >
      <Toolbar className="flex justify-between">
        <SearchField
          value={searchTerm}
          onChange={handleSearch}
          placeholder={t.freightLanes.toolbar.search}
          className="w-[200px] shrink-0"
        />
        <Button color="primary" variant="solid" onPress={handleCreate}>
          <LucidePlus size={16} />
          {t.freightLanes.toolbar.create}
        </Button>
      </Toolbar>

      <Content className="h-full">
        <DataGrid<FreightLaneRecord>
          isLoading={isLoading || isFetching}
          columns={columns}
          dataSource={freightLanes}
          rowKey="id"
          aria-label={t.freightLanes.table.aria}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          stickyHeader
          emptyPanelProps={emptyPanelProps}
        />
      </Content>

      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}

      <FreightLaneDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditFreightLane(null);
        }}
        onSuccess={() => {
          setIsDialogOpen(false);
          setEditFreightLane(null);
          refetch();
        }}
        freightLane={editFreightLane ?? undefined}
      />
    </AsyncBoundary>
  );
};

export const FreightLanesListView: React.FC = () => (
  <TransportationRootLayout className="@FreightLanesListView">
    <FreightLanesListContent />
  </TransportationRootLayout>
);
